import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme, { ThemeColors } from "@insite/mobius/globals/baseTheme";
import getColor from "@insite/mobius/utilities/getColor";
import * as axiomColors from "@optimizely/design-tokens/dist/js/colors";
import { css, FlattenSimpleInterpolation } from "styled-components";

export interface ShellThemeProps {
    theme: ShellTheme;
}

type ShellThemeInferred = typeof shellTheme;

export type ShellTheme = Omit<ShellThemeInferred, "sideBarWidth" | "headerHeight"> &
    Partial<Pick<ShellThemeInferred, "sideBarWidth" | "headerHeight">>;

export interface ShellColors extends ThemeColors {
    custom: {
        mainHeader: string;
        nonmatchingTreeLinks: string;
        accentSecondary: string;
        futurePublish: string;
        draftPage: string;
        neverPublished: string;
        isWaitingForApproval: string;
        isWaitingForApprovalActive: string;
        activeBorder: string;
        activeBackground: string;
        blueBase: string;
        brandPrimaryColor: string;
        buttonPrimaryActiveBackgroundColor: string;
        buttonPrimaryActiveBorderColor: string;
        buttonPrimaryActiveLabelColor: string;
        buttonPrimaryHoverBackgroundColor: string;
        buttonPrimaryHoverBorderColor: string;
        buttonPrimaryHoverLabelColor: string;
        borderDividerColor: string;
        buttonOutlinedActiveBackgroundColor: string;
        buttonOutlinedActiveBorderColor: string;
        buttonOutlinedActiveLabelColor: string;
        buttonOutlinedDisabledBackgroundColor: string;
        buttonOutlinedDisabledBorderColor: string;
        buttonOutlinedDisabledLabelColor: string;
        buttonOutlinedHoverBackgroundColor: string;
        buttonOutlinedHoverBorderColor: string;
        buttonOutlinedHoverLabelColor: string;
        buttonOutlinedFocusBackgroundColor: string;
        buttonOutlinedFocusBorderColor: string;
        buttonOutlinedFocusLabelColor: string;
        buttonOutlinedPressedBackgroundColor: string;
        buttonOutlinedPressedBorderColor: string;
        buttonPrimaryDisabledBackgroundColor: string;
        buttonPrimaryDisabledBorderColor: string;
        buttonPrimaryDisabledLabelColor: string;
        buttonPrimaryFocusBackgroundColor: string;
        buttonPrimaryFocusBorderColor: string;
        buttonPrimaryFocusLabelColor: string;
        buttonPrimaryPressedBackgroundColor: string;
        buttonPrimaryPressedBorderColor: string;
        buttonPrimaryPressedLabelColor: string;
    };
}

const colors: ShellColors = {
    custom: {
        mainHeader: "#5e8b2729",
        nonmatchingTreeLinks: "#bbb",
        accentSecondary: "#cccccc",
        futurePublish: "#ABFF44",
        draftPage: "#ff6a00",
        neverPublished: "#ff2300",
        isWaitingForApproval: "#f7941d",
        isWaitingForApprovalActive: "#f2d7b8",
        activeBorder: axiomColors.brandBlueDark,
        activeBackground: "#5e8b2729",
        blueBase: axiomColors.blueBase,
        brandPrimaryColor: "#ABFF44",
        buttonPrimaryActiveBackgroundColor: "#ABFF44",
        buttonPrimaryActiveBorderColor: "#ABFF44",
        buttonPrimaryActiveLabelColor: "#202320",
        buttonPrimaryHoverBackgroundColor: "#95ff14",
        buttonPrimaryHoverBorderColor: "#95ff14",
        buttonPrimaryHoverLabelColor: "#202320",
        borderDividerColor: axiomColors.borderDividerColor,
        buttonOutlinedActiveBackgroundColor: "#FFF",
        buttonOutlinedActiveBorderColor: "#A1AC8D",
        buttonOutlinedActiveLabelColor: "#202320",
        buttonOutlinedDisabledBackgroundColor: "#FFF",
        buttonOutlinedDisabledBorderColor: "#D8E4CB",
        buttonOutlinedDisabledLabelColor: "#A1AC8D",
        buttonOutlinedHoverBackgroundColor: "#FFF",
        buttonOutlinedHoverBorderColor: "#b8cea0",
        buttonOutlinedHoverLabelColor: "#202320",
        buttonOutlinedFocusBackgroundColor: "#FFF",
        buttonOutlinedFocusBorderColor: "#b8cea0",
        buttonOutlinedFocusLabelColor: "#202320",
        buttonOutlinedPressedBackgroundColor: "#FFF",
        buttonOutlinedPressedBorderColor: "#b8cea0",
        buttonPrimaryDisabledBackgroundColor: "#D8E4CB",
        buttonPrimaryDisabledBorderColor: "#D8E4CB",
        buttonPrimaryDisabledLabelColor: "#A1AC8D",
        buttonPrimaryFocusBackgroundColor: "#95ff14",
        buttonPrimaryFocusBorderColor: "#95ff14",
        buttonPrimaryFocusLabelColor: "#202320",
        buttonPrimaryPressedBackgroundColor: "#95ff14",
        buttonPrimaryPressedBorderColor: "#95ff14",
        buttonPrimaryPressedLabelColor: "#202320",
    },
    primary: {
        main: "#ABFF44",
        contrast: "#fff",
    },
    secondary: {
        main: "#6C757D",
        contrast: "#fff",
    },
    common: {
        background: axiomColors.neutralColorsWhite,
        backgroundContrast: "#4A4A4A",
        accent: "#f4f4f4",
        accentContrast: "#4A4A4A",
        border: "#000000",
        disabled: "#888888",
    },
    text: {
        main: "#202320",
        disabled: "#888",
        accent: "#9B9B9B",
        link: "#226B1E",
    },
    success: {
        main: "#3AB533",
        contrast: "#fff",
    },
    danger: {
        main: "#8F4764",
        contrast: "#fff",
    },
    warning: {
        main: "#F79008",
        contrast: "#fff",
    },
    info: {
        main: "#197A94",
        contrast: "#fff",
    },
} as const;

const controlStyles = (withPadding: boolean) => css`
    border-radius: 4px;
    border: 1px solid ${colors.common.border};
    font-size: 16px;
    line-height: 19px;
    font-weight: 300;
    color: black;
    ${withPadding && "padding: 10px;"}
    width: 100%;
`;

const formField = css`
    margin-top: 10px;
    & .mobiusFileUpload {
        padding: 0 10px;
    }
`;

const buttonStyles: FlattenSimpleInterpolation = css`
    height: 32px;
    border-radius: 3px;
    border-width: 2px;
    padding: 0 16px;
    &:disabled {
        color: #a1ac8d;
    }
`;

const buttonProps: ButtonPresentationProps = {
    buttonType: "solid",
    shape: "rounded",
    typographyProps: {
        transform: "uppercase",
        weight: 700,
    },
    sizeVariant: "medium",
    css: buttonStyles,
};

const shellTheme = {
    sideBarWidth: "340px",
    headerHeight: "40px",
    ...baseTheme,
    colors,
    focus: {
        color: "#ABFF44",
        style: "solid",
        width: "2px",
    },
    button: {
        primary: {
            ...buttonProps,
            color: "primary",
            css: css`
                height: 32px;
                border: 2px solid ${({ theme }) => theme.colors.custom.buttonPrimaryActiveBorderColor};
                border-radius: 4px;
                padding: 0 16px;
                background: ${({ theme }) => theme.colors.custom.buttonPrimaryActiveBackgroundColor};
                color: ${({ theme }) => theme.colors.custom.buttonPrimaryActiveLabelColor};

                &:hover {
                    background: ${({ theme }) => theme.colors.custom.buttonPrimaryHoverBackgroundColor};
                    border: 2px solid ${({ theme }) => theme.colors.custom.buttonPrimaryActiveBorderColor};
                    color: ${({ theme }) => theme.colors.custom.buttonPrimaryHoverLabelColor};
                }
                &:disabled {
                    color: #a1ac8d;
                }
            `,
        },
        secondary: {
            ...buttonProps,
            color: "secondary",
            css: css`
                height: 32px;
                border: 2px solid ${({ theme }) => theme.colors.custom.buttonOutlinedActiveBorderColor};
                border-radius: 4px;
                padding: 0 16px;
                background: ${({ theme }) => theme.colors.custom.buttonOutlinedActiveBackgroundColor};
                color: ${({ theme }) => theme.colors.custom.buttonOutlinedActiveLabelColor};

                &:hover {
                    background: ${({ theme }) => theme.colors.custom.buttonOutlinedHoverBackgroundColor};
                    border: 2px solid ${({ theme }) => theme.colors.custom.buttonHoverActiveBorderColor};
                    color: ${({ theme }) => theme.colors.custom.buttonOutlinedHoverLabelColor};
                }
                &:disabled {
                    color: #a1ac8d;
                }
            `,
        },
        tertiary: {
            ...buttonProps,
            buttonType: "outline",
            css: css`
                ${buttonStyles}
            `,
        },
    },
    checkbox: { defaultProps: { color: "primary.main", iconProps: { src: "Check" } } },
    formField: {
        defaultProps: {
            backgroundColor: "common.background",
            border: "rounded",
            cssOverrides: {
                inputSelect: controlStyles(true),
                formField,
            },
            labelProps: {
                size: "1rem",
                transform: "uppercase",
                weight: "bold",
                lineHeight: "21px",
                css: css`
                    overflow: visible;
                    white-space: nowrap;
                `,
                ellipsis: false,
            },
        },
    },
    datePicker: {
        defaultProps: {
            cssOverrides: {
                inputSelect: controlStyles(false),
                datePicker: css`
                    & .react-datetime-picker {
                        font-size: 13px;
                    }
                `,
            },
        },
    },
    typography: {
        ...baseTheme.typography,
        body: {
            fontFamily: "Inter, Barlow, sans-serif;",
            color: colors.text.main,
            size: "15px",
        },
        h1: {
            ...baseTheme.typography.h1,
            color: colors.text.main,
            size: "40px",
            lineHeight: "100%",
            weight: "600",
        },
        h2: {
            ...baseTheme.typography.h2,
            size: "32px",
            lineHeight: "100%",
            weight: 600,
            css: css`
                margin: 30px 0 10px;
            `,
        },
        h3: {
            ...baseTheme.typography.h3,
            size: "28px",
            lineHeight: "100%",
            weight: 600,
            css: css`
                margin: 30px 0 10px;
            `,
        },
        h4: {
            ...baseTheme.typography.h4,
            size: "24px",
            weight: 600,
        },
        h5: {
            ...baseTheme.typography.h5,
            size: "20px",
            weight: 600,
        },
        h6: {
            ...baseTheme.typography.h6,
            size: "16px",
            weight: 600,
        },
        p: {
            ...baseTheme.typography.p,
            size: "15px",
            lineHeight: "20px",
            weight: 400,
        },
    },
    modal: {
        ...baseTheme.modal,
        defaultProps: {
            cssOverrides: {
                modalTitle: css`
                    background-color: ${getColor("common.backgroundContrast")};
                    padding: 10px 15px;
                    & button {
                        margin: -5px 0; /* makes sure modal title height does not differ if there is or is not a close button. */
                        padding: 0 10px;
                    }
                `,
                headlineTypography: css`
                    margin-bottom: 0;
                    text-transform: capitalize;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                `,
                modalContent: css`
                    padding: 15px;
                `,
                modalBody: css`
                    border-radius: 8px;
                    background-color: ${getColor("common.accent")};
                `,
            },
            closeButtonIconProps: {
                src: "X",
                size: 24,
            },
            headlineTypographyProps: {
                color: "common.background",
                weight: "bold",
                size: "18px",
                lineHeight: 1,
                transform: "uppercase",
            },
            closeButtonProps: {
                buttonType: "solid",
                shape: "rounded",
                // The "solid" button type forces the icon to the button's contrast color, which
                // overrides `closeButtonIconProps.color`. Re-assert the desired icon color here so
                // it wins on both specificity and source order.
                css: css`
                    ${ButtonIcon} {
                        color: ${getColor("text.main")};
                    }
                `,
            },
        },
    },
    toast: {
        ...baseTheme.toast,
        defaultProps: {
            ...baseTheme.toast.defaultProps,
            iconProps: {
                ...baseTheme.toast.defaultProps?.iconProps,
                size: 24,
                css: css`
                    margin-right: 10px;
                    color: ${getColor("text.main")};
                `,
            },
            closeButtonIconProps: {
                ...baseTheme.toast.defaultProps?.closeButtonIconProps,
                css: css`
                    padding: 16px 0;
                    color: ${getColor("text.main")};
                `,
            },
            cssOverrides: {
                toast: css`
                    button {
                        background: none !important;
                        outline: none !important;
                    }
                `,
                toastBody: css`
                    margin: 12px 44px 12px 10px !important;
                `,
            },
        },
    },
} as const;

export default shellTheme;
